import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/SEO";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";
import { ArrowRight } from "lucide-react";

const CATEGORY_META = {
  compatibility: { key: "blog_cat_compatibility", color: "text-xd-cyanHover" },
  installation: { key: "blog_cat_installation", color: "text-xd-orange" },
  news: { key: "blog_cat_news", color: "text-xd-success" },
};

export default function Blog() {
  const { lang, t } = useLang();
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/blog", { params: filter ? { category: filter } : {} }).then((r) => setPosts(r.data));
  }, [filter]);

  return (
    <div className="min-h-screen bg-xd-light">
      <SEO
        title={t("blog_seo_title")}
        description={t("blog_seo_desc")}
        path="/blog"
        lang={lang}
      />
      <Header />
      <section className="bg-xd-dark text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
          <div className="font-mono-tech text-xs uppercase tracking-widest text-xd-cyan">{t("blog_kicker")}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold mt-2">{t("blog_h1")}</h1>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap gap-2 mb-8">
          <button type="button" onClick={() => setFilter("")} data-testid="blog-filter-all" className={`px-3 py-1.5 rounded-full text-sm border ${!filter ? "bg-xd-dark text-white border-xd-dark" : "border-xd-border text-xd-muted hover:border-xd-cyan"}`}>{t("blog_filter_all")}</button>
          {Object.entries(CATEGORY_META).map(([k, v]) => (
            <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`blog-filter-${k}`} className={`px-3 py-1.5 rounded-full text-sm border ${filter === k ? "bg-xd-dark text-white border-xd-dark" : "border-xd-border text-xd-muted hover:border-xd-cyan"}`}>{t(v.key)}</button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((p) => (
            <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group bg-white rounded-xl border border-xd-border overflow-hidden hover:border-xd-cyan transition-colors">
              <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-5">
                <div className={`font-mono-tech text-[10px] uppercase tracking-widest ${CATEGORY_META[p.category]?.color || "text-xd-muted"}`}>{CATEGORY_META[p.category] ? t(CATEGORY_META[p.category].key) : p.category}</div>
                <div className="font-display text-lg font-semibold mt-2 leading-tight group-hover:text-xd-cyanHover line-clamp-2">{p.title}</div>
                <p className="text-sm text-xd-muted mt-2 line-clamp-2">{p.excerpt}</p>
                <div className="text-xs text-xd-cyanHover mt-4 inline-flex items-center gap-1">{t("blog_read")} <ArrowRight className="w-3 h-3" /></div>
              </div>
            </Link>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}
